import { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Props {
  open?: boolean
  isOpen?: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  className?: string
  bodyClassName?: string
  headerActions?: ReactNode
  dataTour?: string
}

export default function Modal({ open, isOpen, onClose, title, children, footer, className = "", bodyClassName = '', headerActions, dataTour }: Props) {
  const { t } = useTranslation('common')
  const isModalOpen = isOpen ?? open ?? false
  // Ignore backdrop clicks from the same gesture that opened the modal
  // (otherwise leave wizards "blink" — open then immediately close).
  const ignoreBackdropUntilRef = useRef(0)

  useEffect(() => {
    if (!isModalOpen) return
    ignoreBackdropUntilRef.current = Date.now() + 300
  }, [isModalOpen])

  if (!isModalOpen) return null

  const handleBackdropClick = () => {
    if (Date.now() < ignoreBackdropUntilRef.current) return
    onClose()
  }

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={handleBackdropClick}>
      <div
        className={`bg-white rounded-lg shadow-lg w-full max-h-[90vh] overflow-hidden flex flex-col ${className || 'max-w-2xl'}`}
        onClick={e => e.stopPropagation()}
        {...(dataTour ? { 'data-tour': dataTour } : {})}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          {title ? (
            <h2 className="text-lg font-semibold">{title}</h2>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {headerActions}
            <button
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label={t('close')}
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className={`flex-1 p-6 min-h-0 ${bodyClassName || 'overflow-y-auto'}`}>
          {children}
        </div>
        {footer && (
          <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
            {footer}
          </div>
        )}
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
